import type { ComputedRef, Ref } from 'vue';
import type { DateTimeModel } from '@/components/fieldsets/StopDateTimeFieldset.vue';
import type { StateTimeZone, StopItem, TimeObject } from '@/models/jobEdit';

import { computed } from 'vue';
import { format } from 'date-fns';
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';

import { statesTimeZonesList, timeZoneDefault } from '@/utils/PortalConstants';

export interface StopDateTimeApiFields {
  time_earliest: string | null;
  time_latest: string | null;
}

export function resolveTimezone(stateCode: string | null | undefined): string {
  if (!stateCode) return timeZoneDefault;
  // tsconfig has noUncheckedIndexedAccess off; widen the value type so the
  // optional chain is meaningful (state codes from the API aren't guaranteed
  // to be a known key).
  const map: Record<string, StateTimeZone | undefined> = statesTimeZonesList;
  return map[stateCode]?.timezone ?? timeZoneDefault;
}

// Reads an ISO instant and returns its wall-clock in the stop's state timezone.
// DST-aware: the same UTC instant resolves to the same wall-clock regardless of the
// browser's local DST state, so a job booked for "09:00 in NSW on 25 Dec" always
// renders as 09:00 whether read in AEST or AEDT.
export function parseISOToLocal(
  isoString: string | null | undefined,
  timezone: string,
): { date: string; time: TimeObject } | null {
  if (!isoString) return null;
  try {
    const utcDate = new Date(isoString);
    if (isNaN(utcDate.getTime())) return null;
    const zoned = toZonedTime(utcDate, timezone);
    return {
      date: format(zoned, 'yyyy-MM-dd'),
      time: {
        hours: zoned.getHours(),
        minutes: zoned.getMinutes(),
        seconds: zoned.getSeconds(),
      },
    };
  } catch {
    return null;
  }
}

// Emits an ISO string whose offset reflects DST as it applies on the *target date*
// in the stop's state timezone, not the offset in force right now. Picking 09:00
// on 25 Dec in NSW produces "...+11:00" (AEDT); 15 Jun produces "...+10:00" (AEST).
// This is what keeps the wall-clock stable across the Apr/Oct DST transitions.
function buildISO(
  date: string | null | undefined,
  time: TimeObject | null | undefined,
  timezone: string,
): string | null {
  if (!date || !time) return null;
  try {
    const paddedHours = String(time.hours).padStart(2, '0');
    const paddedMinutes = String(time.minutes).padStart(2, '0');
    const paddedSeconds = String(time.seconds ?? 0).padStart(2, '0');
    const localString = `${date}T${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
    const utcDate = fromZonedTime(localString, timezone);
    // 'xxx' formats the offset valid for utcDate in `timezone` — i.e. the offset
    // for the target date, automatically AEDT or AEST as applicable.
    const offset = formatInTimeZone(utcDate, timezone, 'xxx');
    return `${localString}${offset}`;
  } catch {
    return null;
  }
}

export function formatStopDateTime(
  isoString: string | null | undefined,
  referenceDate: string | null | undefined,
  state: string | null | undefined,
): string {
  if (!isoString) return '-';
  const parsed = parseISOToLocal(isoString, resolveTimezone(state));
  if (!parsed) return '-';
  const { hours, minutes } = parsed.time;
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const h = hours % 12 || 12;
  const timeStr = `${h}:${String(minutes).padStart(2, '0')} ${ampm}`;
  if (!referenceDate || parsed.date === referenceDate) return timeStr;
  const [refYear] = referenceDate.split('-');
  const [dateYear] = parsed.date.split('-');
  const dateObj = new Date(`${parsed.date}T00:00:00`);
  const shortDate = format(dateObj, 'MMM d');
  if (dateYear !== refYear) return `${timeStr}, ${shortDate}, ${dateYear}`;
  return `${timeStr}, ${shortDate}`;
}

export function useStopDateTime(stop: Ref<Partial<StopItem>>): {
  dateTimeModel: ComputedRef<DateTimeModel>;
  buildApiFields: (model: DateTimeModel) => StopDateTimeApiFields;
} {
  const dateTimeModel = computed<DateTimeModel>(() => {
    const timezone = resolveTimezone(stop.value.state);
    const earliest = parseISOToLocal(
      stop.value.time_earliest ?? null,
      timezone,
    );
    const latest = parseISOToLocal(stop.value.time_latest ?? null, timezone);
    return {
      state: stop.value.state ?? null,
      time_earliest_date: earliest?.date ?? null,
      time_earliest_time: earliest?.time ?? null,
      time_latest_date: latest?.date ?? null,
      time_latest_time: latest?.time ?? null,
    };
  });

  function buildApiFields(model: DateTimeModel): StopDateTimeApiFields {
    const timezone = resolveTimezone(model.state);
    return {
      time_earliest: buildISO(
        model.time_earliest_date,
        model.time_earliest_time,
        timezone,
      ),
      time_latest: buildISO(
        model.time_latest_date,
        model.time_latest_time,
        timezone,
      ),
    };
  }

  return { dateTimeModel, buildApiFields };
}
